import React, { useState, useEffect, useCallback } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import Sidebar from "../Sidebar/Sidebar";
import Navbar from "../Navbar/Navbar";
import "../Assets/Css/Table.css";
import Swal from "sweetalert2";
import { useAuth } from "../AuthProvider";
import { axiosConfig } from "../axiosAPI";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { getApplicationStatusClass } from "../utils/driverStatus";

const formatEgp = (value) =>
  `EGP ${Number(value ?? 0).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;

const formatLabel = (value) =>
  String(value ?? "")
    .replace(/([A-Z])/g, " $1")
    .trim();

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const trimValue = (value) => {
  const text = String(value ?? "").trim();
  return text || "—";
};

const hasText = (value) => String(value ?? "").trim().length > 0;

const DRIVER_DOCUMENTS = [
  { key: "frontNationalIDPath", title: "Front National ID" },
  { key: "backNationalIDPath", title: "Back National ID" },
  { key: "frontDriverLicensePath", title: "Front Driver License" },
  { key: "backDriverLicensePath", title: "Back Driver License" },
  { key: "frontCarLicensePath", title: "Front Car License" },
  { key: "backCarLicensePath", title: "Back Car License" },
  { key: "criminalRecordPath", title: "Criminal Record" },
  { key: "drugTestPath", title: "Drug Test" },
];

function Details() {
  const { uuid: routeDriverUuid } = useParams();
  const location = useLocation();
  const auth = useAuth();
  const navigate = useNavigate();
  const [driver, setDriver] = useState(null);
  const [loading, setLoading] = useState(true);
  const driverUuid = driver?.uuid || routeDriverUuid;
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [imageModalVisible, setImageModalVisible] = useState(false);
  const [selectedImage, setSelectedImage] = useState({ url: "", title: "" });

  const returnTo = location.state?.returnTo || "/Driver";

  useEffect(() => {
    const item = location.state?.item;
    const itemIsThisDriver =
      item && String(item.uuid) === String(routeDriverUuid);

    if (itemIsThisDriver) {
      setDriver(item);
    }

    if (!routeDriverUuid || !auth.token) {
      if (!itemIsThisDriver) {
        setDriver(null);
      }
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const response = await axiosConfig.get(
          `${process.env.REACT_APP_BASE_URL}/users/drivers/${routeDriverUuid}`,
          {
            headers: {
              Authorization: "Bearer " + auth.token,
              "x-api-key": "fab6c94fc0044b53bf5e886a8fef1325",
            },
          }
        );
        if (!cancelled) {
          setDriver(response.data.data ?? null);
        }
      } catch (error) {
        if (!cancelled) {
          if (!itemIsThisDriver) {
            setDriver(null);
          }
          Swal.fire({
            icon: "error",
            title: "Oops...",
            text: "Failed to load driver details!",
          });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [location.state?.item, routeDriverUuid, auth.token]);

  const fetchDriverAnalytics = useCallback(async () => {
    if (!driverUuid) return;
    try {
      setAnalyticsLoading(true);
      const response = await axiosConfig.get(
        `${process.env.REACT_APP_BASE_URL}/dashboard/driver-analytics`,
        {
          params: { driverUuid },
          headers: {
            Authorization: "Bearer " + auth.token,
            "x-api-key": "fab6c94fc0044b53bf5e886a8fef1325",
          },
        }
      );
      setAnalytics(response.data.data);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Failed to load driver analytics!",
      });
      setAnalytics(null);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [driverUuid, auth.token]);

  useEffect(() => {
    fetchDriverAnalytics();
  }, [fetchDriverAnalytics]);

  const goBackToDrivers = () => {
    if (location.state?.returnTo) {
      navigate(
        returnTo,
        location.state.returnState
          ? { state: location.state.returnState }
          : undefined
      );
    } else {
      navigate(-1);
    }
  };

  const approveUser = async (uuid) => {
    try {
      await axiosConfig.put(
        `${process.env.REACT_APP_BASE_URL}/users/drivers/${uuid}/approve`,
        {},
        {
          headers: {
            Authorization: "Bearer " + auth.token,
            "Content-Type": "application/json",
            "x-api-key": "fab6c94fc0044b53bf5e886a8fef1325",
          },
        }
      );
      await Swal.fire({
        icon: "success",
        title: "Success!",
        text: "Driver approved successfully!",
      });
      navigate(returnTo);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Failed to approve driver!",
      });
    }
  };

  const rejectUser = async (uuid) => {
    try {
      await axiosConfig.put(
        `${process.env.REACT_APP_BASE_URL}/users/drivers/${uuid}/reject`,
        {},
        {
          headers: {
            Authorization: "Bearer " + auth.token,
            "Content-Type": "application/json",
            "x-api-key": "fab6c94fc0044b53bf5e886a8fef1325",
          },
        }
      );
      await Swal.fire({
        icon: "success",
        title: "Success!",
        text: "Driver rejected successfully!",
      });
      navigate(returnTo);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Failed to reject driver!",
      });
    }
  };

  const handleImageClick = (imagePath, title) => {
    if (imagePath) {
      setSelectedImage({
        url: `${process.env.REACT_APP_BASE_URL_IMG}${imagePath}`,
        title,
      });
      setImageModalVisible(true);
    }
  };

  const ImageThumbnail = ({ imagePath, title, altText }) => {
    const imageUrl = imagePath
      ? `${process.env.REACT_APP_BASE_URL_IMG}${imagePath}`
      : null;

    return (
      <div className="image-thumbnail-container">
        <div
          className="image-thumbnail-wrapper"
          onClick={() => handleImageClick(imagePath, title)}
        >
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={altText || title}
              className="image-thumbnail"
              onError={(e) => {
                e.target.style.display = "none";
                e.target.nextSibling.style.display = "flex";
              }}
            />
          ) : null}
          <div
            className="image-placeholder"
            style={{ display: imageUrl ? "none" : "flex" }}
          >
            <i className="fa fa-image"></i>
            <span>No Image</span>
          </div>
        </div>
        <p className="image-title">{title}</p>
      </div>
    );
  };

  const InfoField = ({ label, value, children }) => (
    <div className="passenger-info-field">
      <span className="passenger-info-label">{label}</span>
      <span className="passenger-info-value">{children ?? trimValue(value)}</span>
    </div>
  );

  const accountStateClass =
    driver?.stateLocalized?.toLowerCase() === "enabled"
      ? "passenger-status-badge passenger-status-badge--enabled"
      : "passenger-status-badge passenger-status-badge--disabled";

  const profileImageUrl = driver?.profilePicturePath
    ? `${process.env.REACT_APP_BASE_URL_IMG}${driver.profilePicturePath}`
    : null;

  const uploadedDocumentsCount = DRIVER_DOCUMENTS.filter(({ key }) =>
    hasText(driver?.[key])
  ).length;

  return (
    <>
      <div className="main">
        <div className="sidebar">
          <Sidebar />
        </div>
        <div className="dev-table">
          <Navbar />
          <div className="table-card">
            <div className="details passenger-details driver-details">
              <div className="row">
                <div className="col-md-12">
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
                    <div className="d-flex align-items-center gap-3">
                      <a
                        className="edit"
                        href={returnTo}
                        onClick={(e) => {
                          e.preventDefault();
                          goBackToDrivers();
                        }}
                        style={{ cursor: "pointer" }}
                      >
                        <i className="fa fa-arrow-left me-1"></i>
                        Back
                      </a>
                      <h1 className="mb-0">Driver Profile</h1>
                    </div>

                    {driver ? (
                      <div className="driver-header-actions">
                        <span className={getApplicationStatusClass(driver.applicationStatus)}>
                          {trimValue(
                            driver.applicationStatusLocalized ||
                              driver.applicationStatus
                          )}
                        </span>
                        {driver.applicationStatus === "Pending" ? (
                          <div className="driver-approval-actions">
                            <button
                              type="button"
                              className="driver-approve-btn"
                              onClick={() => approveUser(driver.uuid)}
                            >
                              <i className="fa fa-check me-1"></i>
                              Approve
                            </button>
                            <button
                              type="button"
                              className="driver-reject-btn"
                              onClick={() => rejectUser(driver.uuid)}
                            >
                              <i className="fa fa-times me-1"></i>
                              Reject
                            </button>
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </div>

                {loading ? (
                  <div className="col-md-12">
                    <p className="passenger-details-empty">Loading driver profile...</p>
                  </div>
                ) : !driver ? (
                  <div className="col-md-12">
                    <p className="passenger-details-empty">Driver not found.</p>
                  </div>
                ) : (
                  <>
                    <div className="col-md-12">
                      <div className="passenger-profile-card">
                        <div className="passenger-profile-card__header">
                          <div
                            className={`driver-profile-avatar${
                              profileImageUrl ? " driver-profile-avatar--photo" : ""
                            }`}
                            onClick={() =>
                              handleImageClick(
                                driver.profilePicturePath,
                                "Profile Picture"
                              )
                            }
                            role={profileImageUrl ? "button" : undefined}
                            tabIndex={profileImageUrl ? 0 : undefined}
                            onKeyDown={(event) => {
                              if (
                                profileImageUrl &&
                                (event.key === "Enter" || event.key === " ")
                              ) {
                                event.preventDefault();
                                handleImageClick(
                                  driver.profilePicturePath,
                                  "Profile Picture"
                                );
                              }
                            }}
                          >
                            {profileImageUrl ? (
                              <img
                                src={profileImageUrl}
                                alt={trimValue(driver.fullName)}
                                onError={(e) => {
                                  e.target.style.display = "none";
                                }}
                              />
                            ) : (
                              <i className="fa fa-car"></i>
                            )}
                          </div>
                          <div>
                            <h2 className="passenger-profile-name">
                              {trimValue(driver.fullName)}
                            </h2>
                            <p className="passenger-profile-subtitle">
                              {trimValue(driver.userType || "Driver")}
                              {driver.rating != null ? (
                                <>
                                  {" · "}
                                  <i className="fa fa-star driver-rating-star"></i>{" "}
                                  {Number(driver.rating).toFixed(1)}
                                </>
                              ) : null}
                            </p>
                          </div>
                          {driver.stateLocalized ? (
                            <span className={`${accountStateClass} ms-auto`}>
                              {driver.stateLocalized}
                            </span>
                          ) : null}
                        </div>

                        <div className="passenger-info-grid">
                          <InfoField label="Phone Number" value={driver.phoneNumber} />
                          <InfoField
                            label="Phone Verified"
                            value={driver.phoneNumberConfirmed ? "Yes" : "No"}
                          />
                          <InfoField label="National ID" value={driver.nationalID} />
                          <InfoField
                            label="Date Joined"
                            value={formatDateTime(driver.dateCreated)}
                          />
                          <InfoField
                            label="Last Login"
                            value={formatDateTime(driver.dateLastLogin)}
                          />
                          <InfoField label="Driver ID" value={driver.uuid} />
                        </div>
                      </div>
                    </div>

                    <div className="col-md-12 mt-4">
                      <div className="passenger-section-card">
                        <h2 className="passenger-section-title">
                          <i className="fa fa-car me-2"></i>
                          Vehicle Information
                        </h2>
                        <div className="passenger-info-grid">
                          <InfoField label="Car Model" value={driver.carModel} />
                          <InfoField label="Car License" value={driver.carLicense} />
                          <InfoField
                            label="Car Plate Number"
                            value={driver.carPlateNumber}
                          />
                          <InfoField label="Car Color">
                            <span className="driver-color-value">
                              <span
                                className="driver-color-swatch"
                                style={{
                                  backgroundColor: driver.carColor || "#000000",
                                }}
                              ></span>
                              {trimValue(driver.carColor)}
                            </span>
                          </InfoField>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-12 mt-4">
                      <div className="passenger-section-card driver-analytics-section">
                        <h2 className="passenger-section-title mb-0">
                          <i className="fa fa-bar-chart me-2"></i>
                          Driver Analytics
                        </h2>
                        <div className="driver-stats-cards mt-4">
                          <div className="row g-3">
                            <div className="col-md-4 col-sm-6">
                              <div className="card-details driver-stat-card driver-analytics-completed">
                                <h2>
                                  Completed Rides
                                  <span>
                                    {analyticsLoading
                                      ? "—"
                                      : analytics?.completedCount ?? 0}
                                  </span>
                                </h2>
                              </div>
                            </div>
                            <div className="col-md-4 col-sm-6">
                              <div className="card-details driver-stat-card driver-analytics-cancelled">
                                <h2>
                                  Cancelled Rides
                                  <span>
                                    {analyticsLoading
                                      ? "—"
                                      : analytics?.cancelledCount ?? 0}
                                  </span>
                                </h2>
                              </div>
                            </div>
                            <div className="col-md-4 col-sm-6">
                              <div className="card-details driver-stat-card driver-analytics-rating">
                                <h2>
                                  Average Rating
                                  <span>
                                    {analyticsLoading
                                      ? "—"
                                      : Number(analytics?.averageRating ?? 0).toFixed(1)}
                                  </span>
                                </h2>
                              </div>
                            </div>
                            <div className="col-md-6 col-sm-6">
                              <div className="card-details driver-stat-card driver-analytics-gross">
                                <h2>
                                  Gross Fares
                                  <span>
                                    {analyticsLoading
                                      ? "—"
                                      : formatEgp(analytics?.grossFares)}
                                  </span>
                                </h2>
                              </div>
                            </div>
                            <div className="col-md-6 col-sm-6">
                              <div className="card-details driver-stat-card driver-analytics-net">
                                <h2>
                                  Net Driver Earnings
                                  <span>
                                    {analyticsLoading
                                      ? "—"
                                      : formatEgp(analytics?.netDriverEarnings)}
                                  </span>
                                </h2>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="row driver-analytics-breakdowns g-3 mt-1">
                          <div className="col-md-6">
                            <div className="driver-analytics-table-card">
                              <h3>Ride Type Breakdown</h3>
                              {analyticsLoading ? (
                                <p className="driver-analytics-empty">Loading...</p>
                              ) : analytics?.rideTypeBreakdown?.length ? (
                                <table className="driver-analytics-table">
                                  <thead>
                                    <tr>
                                      <th>Ride Type</th>
                                      <th>Count</th>
                                      <th>Gross Fares</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {analytics.rideTypeBreakdown.map((row) => (
                                      <tr key={row.rideType}>
                                        <td>{formatLabel(row.rideType)}</td>
                                        <td>{row.count}</td>
                                        <td>{formatEgp(row.grossFares)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              ) : (
                                <p className="driver-analytics-empty">
                                  No ride type data available.
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="driver-analytics-table-card">
                              <h3>Payment Type Breakdown</h3>
                              {analyticsLoading ? (
                                <p className="driver-analytics-empty">Loading...</p>
                              ) : analytics?.paymentTypeBreakdown?.length ? (
                                <table className="driver-analytics-table">
                                  <thead>
                                    <tr>
                                      <th>Payment Type</th>
                                      <th>Count</th>
                                      <th>Gross Fares</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {analytics.paymentTypeBreakdown.map((row) => (
                                      <tr key={row.paymentType}>
                                        <td>{formatLabel(row.paymentType)}</td>
                                        <td>{row.count}</td>
                                        <td>{formatEgp(row.grossFares)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              ) : (
                                <p className="driver-analytics-empty">
                                  No payment type data available.
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-12 mt-4">
                      <div className="passenger-section-card">
                        <div className="passenger-section-card__header">
                          <h2 className="passenger-section-title mb-0">
                            <i className="fa fa-folder-open me-2"></i>
                            Documents & Files
                          </h2>
                          <span
                            className={
                              uploadedDocumentsCount === DRIVER_DOCUMENTS.length
                                ? "passenger-tag passenger-tag--success"
                                : uploadedDocumentsCount > 0
                                  ? "passenger-tag passenger-tag--warning"
                                  : "passenger-tag passenger-tag--neutral"
                            }
                          >
                            {uploadedDocumentsCount}/{DRIVER_DOCUMENTS.length} uploaded
                          </span>
                        </div>

                        <div className="driver-documents-grid">
                          {DRIVER_DOCUMENTS.map(({ key, title }) => {
                            const imagePath = driver[key];
                            return hasText(imagePath) ? (
                              <ImageThumbnail
                                key={key}
                                imagePath={imagePath}
                                title={title}
                                altText={title}
                              />
                            ) : (
                              <div key={key} className="passenger-document-missing">
                                <span>{title}</span>
                                <small>Not uploaded</small>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog
        visible={imageModalVisible}
        modal
        header={selectedImage.title}
        style={{ width: "90vw", maxWidth: "900px" }}
        onHide={() => setImageModalVisible(false)}
        footer={
          <div className="modal-footer">
            <Button
              label="Close"
              icon="pi pi-times"
              onClick={() => setImageModalVisible(false)}
              className="p-button-text"
            />
            <Button
              label="Open in New Tab"
              icon="pi pi-external-link"
              onClick={() => window.open(selectedImage.url, "_blank")}
            />
          </div>
        }
      >
        <div className="image-modal-content">
          <img
            src={selectedImage.url}
            alt={selectedImage.title}
            className="modal-image img-fluid"
            style={{ maxHeight: "70vh", width: "100%", objectFit: "contain" }}
            onError={(e) => {
              e.target.style.display = "none";
              e.target.nextSibling.style.display = "flex";
            }}
          />
          <div className="image-error-placeholder" style={{ display: "none" }}>
            <i className="fa fa-exclamation-triangle fa-3x"></i>
            <p>Failed to load image</p>
          </div>
        </div>
      </Dialog>
    </>
  );
}

export default Details;
